/**
 * Which modules a dashboard screen needs, and who has them.
 *
 * Screens used to be gated by plan NAME (`plans: ['PREMIUM','STANDARD']` in
 * routes.ts). With five tiers sold through two payment routes, those lists
 * became unmaintainable. Now a screen names the modules it needs, a plan grants
 * modules, and the server says which modules a shop has:
 *
 *   GET /shop/entitlements/{shopId}  →  { modules: { cloud_pos: {...}, … } }
 *
 * The canonical copies live in infrastructure/lambda/entitlements
 * (routeModules.json, planModules.json). A test there checks that these stay
 * in step, and that every legacy plan still opens exactly the screens it opens
 * today — see docs/feature-pos-subscription-stripe.md §3.1.
 */

/** A screen opens if the shop has ANY of these. Screens not listed are never gated. */
export const ROUTE_MODULES: Record<string, string[]> = {
  // '/supplier' is deliberately NOT gated (21 Oct 2026): stock intakes and
  // stock levels never were, and the intake editor creates suppliers inline,
  // so gating the list only hid suppliers a shop could already add.
  '/customers': ['cloud_pos'],
  '/users': ['cloud_pos'],
  '/report': ['cloud_pos'],
  '/transaction': ['cloud_pos'],
  '/discounts': ['cloud_pos'],
  '/offer': ['cloud_pos'],
  // The tablet tier needs these two to run a HandPad: staff PINs, and the
  // shop PIN and token a tablet connects with.
  '/employees': ['cloud_pos', 'tablet_pos'],
  '/register-info': ['cloud_pos', 'tablet_pos'],
  // Tables belongs to the menu and kitchen products. Premium (which includes
  // KDS) and the old Menu plan both keep it, exactly as before.
  '/tables': ['menu', 'kds'],
};

/** What each plan grants. Mirrors PaymentConfig/PLAN_MODULES. */
export const PLAN_MODULES: Record<string, string[]> = {
  TABLET: ['tablet_pos'],
  BUSINESS: ['tablet_pos', 'cloud_pos', 'menu', 'kds'],
  COMPLETE: ['tablet_pos', 'cloud_pos', 'menu', 'kds', 'ats'],
  ATS: ['ats'],
  STANDARD: ['tablet_pos', 'cloud_pos'],
  PREMIUM: ['tablet_pos', 'cloud_pos', 'kds'],
  MENU: ['menu'],
};

/** The shape the entitlements endpoint returns. */
export type ShopModules = Record<string, { state: string; until: number | null }> | null;

/**
 * May this shop open this screen?
 *
 * `modules` is null when the answer could not be fetched. That is NOT treated
 * as "no access": a dashboard that cannot check should not lock a paying
 * customer out of their own business. The whole-app subscription gate is
 * unchanged and still applies.
 */
export function canOpen(path: string, modules: ShopModules): boolean {
  const required = ROUTE_MODULES[path];
  if (!required || required.length === 0) return true;
  if (!modules) return true;
  return required.some((m) => !!modules[m]);
}

/** Screens a plan opens — used by the equivalence test and for support questions. */
export function screensForPlan(planId: string): string[] {
  const granted = PLAN_MODULES[planId] ?? [];
  const asModules = Object.fromEntries(granted.map((m) => [m, { state: 'active', until: null }]));
  return Object.keys(ROUTE_MODULES).filter((path) => canOpen(path, asModules)).sort();
}
